import React, { useContext, useEffect, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import ProviderComponent from "../../Atoms/ProviderComponent";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import { useDashboard } from "../../../contexts/DashboardContext";
import usePermissions from "../../../hooks/userPermissions";
import { useAgent } from "../../../contexts/AgentsContext";
import CustomTooltip from "../../Atoms/CustomTooltip";
import Seperator from "../../Atoms/Seperator";
import { capitalizeFirstLetter } from "../../../util";
import { AuthContext } from "../../../AuthContext";
import SkillsDisplay from "../SkillsDisplay/SkillsDisplay";
import "../css/agent.card.two.css";
import "../../Atoms/agent.card.css";

const AgentCard = ({ agent, onEdit, onCardClick }) => {
  const { canAgentUpdate, canAgentDelete } = usePermissions();

  const { organisationId, projectId, authData } = useContext(AuthContext);
  const { fetchAgentsSilently, deleteAgent, selectedAgent } = useAgent();
  const { showConfirmationModal } = useDashboard();
  const { access_token, token_type } = authData;
  const [cardIndustryPopupEl, setCardIndustryPopupEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSkillsTooltip, setShowSkillsTooltip] = useState(false);

  const skills =
    agent?.agent_metadata?.a2a?.skills?.map((skill) => skill?.name) || [];

  // Mutation for deleting agent
  const mutation = useMutation({
    mutationFn: () => deleteAgent(agent?.agent_id),
    onSettled: () => fetchAgentsSilently(),
  });

  const handleMenuClose = () => {
    // Menu close functionality can be added here if needed
  };

  const handleDeleteMenuClick = (e) => {
    e.stopPropagation();
    showConfirmationModal({
      title: "Delete Agent",
      body: "Are you sure you want to delete agent?",
      onOkLabel: "Confirm",
      onOk: async () => {
        await mutation.mutateAsync({
          id: agent?.agent_id,
          organisationId,
          projectId,
          access_token,
          token_type,
        });
      },
    });
    handleMenuClose();
    setMenuOpen(false);
  };

  const showAllIndustriesSectors = Boolean(cardIndustryPopupEl);
  const handleHover = (e) => {
    if (e.currentTarget.dataset.name === "industryTooltip") {
      setCardIndustryPopupEl(e.currentTarget);
    }
  };

  const handleClose = (e) => {
    if (e.currentTarget.dataset.name === "industryTooltip") {
      setCardIndustryPopupEl(null);
    }
  };

  useEffect(() => {
    // Only add the listener when the menu is open
    if (menuOpen) {
      const handleClickOutside = (event) => {
        // Check if the click is outside the menu
        const menuElement = document.querySelector(".menu-container");
        const iconElement = document.querySelector(".menuVertIcon");

        if (
          menuElement &&
          iconElement &&
          !menuElement.contains(event.target) &&
          !iconElement.contains(event.target)
        ) {
          setMenuOpen(false);
        }
      };

      // Add event listener
      document.addEventListener("mousedown", handleClickOutside);

      // Cleanup
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [menuOpen]);

  const industryInfo = agent?.agent_metadata?.a2a?.industry_info || [];

  return (
    <>
      <Card
        className="marketPlaceCard agent-card-css"
        onClick={onCardClick}
        sx={{
          backgroundColor:
            selectedAgent?.agent_id === agent?.agent_id
              ? "#F7F5F9"
              : "var(--db-primary-white)",
          overflow: "initial",
        }}
      >
        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div>
            <Box className="marketPlaceCardHeader ">
              <Typography className="marketPlaceCardTitle" component="div">
                {agent?.agent_name || ""}
              </Typography>
            </Box>

            <div
              className="marketPlaceCardStatus"
              style={{
                color:
                  agent?.approval_status &&
                  agent?.approval_status?.toLowerCase() === "published"
                    ? "var(--db-primary-green-four)"
                    : "var(--db-secondary-gray-seven)",
              }}
            >
              {capitalizeFirstLetter(agent?.approval_status || "")}
            </div>
          </div>
          <div className="marketPlaceCardDesc" variant="body2">
            <ReactMarkdown>{agent?.agent_description || ""}</ReactMarkdown>
          </div>

          <Seperator
            styles={{
              border: 0,
              borderTop: "1px solid var(--db-secondary-gray-four)",
              strokeWidth: "O.5px",
              margin: "0",
            }}
          />
          <div spacing={1} style={{ height: "50px", position: "relative" }}>
            {industryInfo && industryInfo?.length > 0 ? (
              <>
                {industryInfo?.slice(0, 2)?.map((item, index) => {
                  const industry = item?.industry;
                  const sectors = item?.sector || [];

                  return (
                    <div key={index}>
                      <div
                        key={index}
                        className="wd-100 mb-2"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <div
                          className="mas-industries-sector-div"
                          style={{
                            width: `${
                              index === 1 && industryInfo?.length > 2
                                ? "80%"
                                : "99%"
                            }`,
                          }}
                        >
                          {industry}
                          {sectors && sectors?.length > 0 && (
                            <> | {sectors.join(", ")}</>
                          )}
                        </div>
                        {index === 1 && industryInfo?.length > 2 && (
                          <div
                            className="marketPlaceCardMoreCount ms-2"
                            onMouseOver={handleHover}
                            onMouseLeave={handleClose}
                            data-name="industryTooltip"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            +{industryInfo?.length - 2} more
                          </div>
                        )}
                        {showAllIndustriesSectors && (
                          <div className="industryTooltip-hover">
                            <CustomTooltip
                              containerClsNames="marketplace-tooltip-container"
                              txtClsNames="marketplace-tooltip-txt"
                              arrowClsNames="marketplace-tooltip-arrow-ind-sec"
                              tooltipTitle="Industries and Sectors"
                              content={
                                <ul>
                                  {industryInfo?.map((item, index) => (
                                    <li key={index}>
                                      {item?.industry}
                                      {item?.sector &&
                                        item?.sector.length > 0 && (
                                          <> | {item?.sector.join(", ")}</>
                                        )}
                                    </li>
                                  ))}
                                </ul>
                              }
                              tooltipTitleClass="mas-tooltip-title"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                <div className="marketPlaceCardIndustry">
                  No industry information available{" "}
                </div>
              </>
            )}
          </div>

          <SkillsDisplay
            skills={skills}
            onShowTooltip={() => setShowSkillsTooltip(true)}
            onHideTooltip={() => setShowSkillsTooltip(false)}
          />

          {showSkillsTooltip && skills.length > 3 && (
            <div className="skills-tooltip-container">
              <div className="skills-tooltip-wrapper">
                <CustomTooltip
                  containerClsNames="marketplace-tooltip-container"
                  txtClsNames="marketplace-tooltip-txt"
                  arrowClsNames="marketplace-tooltip-arrow-skills"
                  tooltipTitle="All Skills"
                  component={
                    <ul className="skills-tooltip-list">
                      {skills.map((skill, index) => (
                        <li key={index} className="skills-tooltip-list-item">
                          {skill}
                        </li>
                      ))}
                    </ul>
                  }
                  tooltipTitleClass="mas-tooltip-title"
                />
              </div>
            </div>
          )}

          <div className="wd-100 d-flex">
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"Provider"}
              description={agent?.agent_metadata?.a2a?.provider || ""}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"Version"}
              description={agent?.agent_metadata?.a2a?.version || ""}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
          </div>
          <div className="wd-100 d-flex">
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"Point of Contact"}
              description={agent?.point_of_contact || ""}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"Created on"}
              description={format(agent?.created_on, "MM/dd/yy") || ""}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
          </div>
        </CardContent>
        {
          (canAgentUpdate || canAgentDelete) && (
            <div className="menuVertIcon">
              <MoreVertIcon
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen(!menuOpen);
                }}
              />
              {menuOpen && (
                <div className="menu-container">
                  {canAgentUpdate && (
                    <div
                      className="mas-item-card"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        onEdit(e);
                        handleMenuClose();
                      }}
                      clas
                    >
                      Edit
                    </div>
                  )}
                  {canAgentDelete && (
                    <div
                      className="mas-item-card"
                      onClick={(e) => {
                        handleDeleteMenuClick(e);
                      }}
                    >
                      Delete
                    </div>
                  )}
                </div>
              )}
            </div>
          )
          // )
          // </IconButton >
        }
      </Card>
    </>
  );
};

export default AgentCard;
